import React, { useState } from "react";

const AutocompleteFilter = ({ options, label, value, onChange, icon }) => {
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [showOptions, setShowOptions] = useState(false);

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        onChange(inputValue);

        const matches = options.filter(
            (option) =>
                option &&
                option.toLowerCase().includes(inputValue?.toLowerCase() || "")
        );
        setFilteredOptions(matches);
        setShowOptions(inputValue !== "");
    };

    const handleOptionClick = (option) => {
        onChange(option);
        setShowOptions(false);
    };

    const handleInputBlur = () => {
        setTimeout(() => {
            setShowOptions(false);
        }, 150);
    };

    return (
        <div className="relative">
            <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                {icon && <i className={`${icon} mr-1`}></i>}
                {label}
            </label>
            <div className="flex">
                <input
                    type="text"
                    value={value}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onFocus={() => setShowOptions(value !== "")}
                    placeholder={`Buscar ${label.toLowerCase()}...`}
                    autoComplete="off"
                />
                <div className="px-3 py-2 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-r-lg flex items-center">
                    <i className="fas fa-search text-gray-500 dark:text-gray-400 text-sm"></i>
                </div>
            </div>
            {showOptions && (
                <div
                    className="absolute z-10 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                >
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <button
                                key={index}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 truncate"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleOptionClick(option);
                                }}
                            >
                                {option}
                            </button>
                        ))
                    ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            <i className="fas fa-search mr-2"></i>
                            Sin resultados
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export function ProductFilter({
    filters,
    handleFilterChange,
    handleFilterChangeForAutocomplete,
    getUniqueValues,
    products,
    prices,
}) {

    const saleNameOptions = [
        ...new Set(products.map((product) => product.saleName?.toLowerCase())),
    ];
    const skuOptions = [...new Set(products.map((product) => product.sku))];

    const purchaseNameOptions = [
        ...new Set(
            products.map((product) => product.purchaseName?.toLowerCase())
        ),
    ];

    return (
        <div className="mb-4">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-t-lg">
                <h5 className="mb-0 font-bold flex items-center">
                    <i className="fas fa-filter mr-2"></i>
                    Filtros de Búsqueda
                </h5>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                            <i className="fas fa-hashtag mr-1"></i>
                            Código
                        </label>
                        <select
                            name="code"
                            value={filters.code}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onChange={handleFilterChange}
                        >
                            <option value="">Todos</option>
                            {getUniqueValues("code").map((code) => (
                                <option key={code} value={code}>
                                    {code}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <AutocompleteFilter
                            label="SKU"
                            icon="fas fa-barcode"
                            options={skuOptions}
                            value={filters.sku}
                            onChange={(value) =>
                                handleFilterChangeForAutocomplete("sku", value)
                            }
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                            <i className="fas fa-tag mr-1"></i>
                            Marca
                        </label>
                        <select
                            name="productBrandName"
                            value={filters.productBrandName}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onChange={handleFilterChange}
                        >
                            <option value="">Todos</option>
                            {getUniqueValues("productBrandName").map(
                                (brand) => (
                                    <option key={brand} value={brand}>
                                        {brand}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                            <i className="fas fa-dollar-sign mr-1"></i>
                            Precio Tienda
                        </label>
                        <select
                            name="priceSale"
                            value={filters.priceSale}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onChange={handleFilterChange}
                        >
                            <option value="">Todos</option>
                            {prices.map((price, index) => (
                                <option key={index} value={price}>
                                    {price}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                            <i className="fas fa-toggle-on mr-1"></i>
                            Estado
                        </label>
                        <select
                            name="available"
                            value={filters.available}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onChange={handleFilterChange}
                        >
                            <option value="">Todos</option>
                            <option value="true">Activo</option>
                            <option value="false">No Activo</option>
                        </select>
                    </div>

                    <div className="flex items-end">
                        <button
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium text-sm transition"
                            onClick={() => {
                                handleFilterChange({
                                    target: { name: "code", value: "" },
                                });
                                handleFilterChange({
                                    target: { name: "sku", value: "" },
                                });
                                handleFilterChange({
                                    target: {
                                        name: "productBrandName",
                                        value: "",
                                    },
                                });
                                handleFilterChange({
                                    target: { name: "priceSale", value: "" },
                                });
                                handleFilterChange({
                                    target: { name: "available", value: "" },
                                });
                                handleFilterChangeForAutocomplete(
                                    "purchaseName",
                                    ""
                                );
                                handleFilterChangeForAutocomplete(
                                    "saleName",
                                    ""
                                );
                            }}
                        >
                            <i className="fas fa-eraser mr-1"></i>
                            Limpiar
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                    <div>
                        <AutocompleteFilter
                            label="Nombre de Compra"
                            icon="fas fa-shopping-cart"
                            options={purchaseNameOptions}
                            value={filters.purchaseName}
                            onChange={(value) =>
                                handleFilterChangeForAutocomplete(
                                    "purchaseName",
                                    value
                                )
                            }
                        />
                    </div>
                    <div>
                        <AutocompleteFilter
                            label="Nombre de Venta"
                            icon="fas fa-store"
                            options={saleNameOptions}
                            value={filters.saleName}
                            onChange={(value) =>
                                handleFilterChangeForAutocomplete(
                                    "saleName",
                                    value
                                )
                            }
                        />
                    </div>
                </div>

                <div className="mt-4">
                    <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg py-2 px-4">
                        <small className="text-blue-800 dark:text-blue-300">
                            <i className="fas fa-info-circle mr-1"></i>
                            <strong>Tip:</strong> Usa los campos de búsqueda
                            para filtrar productos por nombre. Los filtros
                            se aplican automáticamente mientras escribes.
                        </small>
                    </div>
                </div>
            </div>
        </div>
    );
}
